import React from 'react';
import { ProductCategory } from '../../types/product';
import { CATEGORIES } from '../../types/product';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../common';

interface ProductFilterProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory: ProductCategory | null;
  onCategoryChange: (category: ProductCategory | null) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  organicOnly: boolean;
  onOrganicChange: (organic: boolean) => void;
  onClearFilters: () => void;
}

const ProductFilter: React.FC<ProductFilterProps> = ({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  sortBy,
  onSortChange,
  organicOnly,
  onOrganicChange,
  onClearFilters,
}) => {
  const hasActiveFilters = searchQuery || selectedCategory || organicOnly;

  return (
    <div className="bg-dark-dark dark:bg-white rounded-xl shadow-sm border border-dark-light dark:border-gray-100 p-4 mb-6 md:mb-8 transition-colors duration-300">
      <div className="flex flex-col lg:flex-row gap-3 md:gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500 transition-colors duration-300" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-dark dark:border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-primary focus:border-transparent transition-all bg-dark dark:bg-gray-50 text-white dark:text-gray-800 placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
          <button
            onClick={() => onCategoryChange(null)}
            className={`px-3 py-2 md:px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-sm md:text-base ${
              !selectedCategory
                ? 'bg-accent dark:bg-primary text-white'
                : 'bg-dark-light dark:bg-gray-100 text-gray-300 dark:text-gray-600 hover:bg-dark dark:hover:bg-gray-200 transition-colors duration-300'
            }`}
          >
            All
          </button>
          {CATEGORIES.map((category) => (
            <button
              key={category.id}
              onClick={() => onCategoryChange(category.id)}
              className={`px-3 py-2 md:px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-sm md:text-base ${
                selectedCategory === category.id
                  ? 'bg-accent dark:bg-primary text-white'
                  : 'bg-dark-light dark:bg-gray-100 text-gray-300 dark:text-gray-600 hover:bg-dark dark:hover:bg-gray-200 transition-colors duration-300'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Second Row */}
      <div className="flex flex-wrap items-center gap-3 md:gap-4 mt-3 md:mt-4 pt-3 md:pt-4 border-t border-dark-light dark:border-gray-100 transition-colors duration-300">
        {/* Sort */}
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500 transition-colors duration-300" />
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="border border-dark dark:border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-primary bg-dark dark:bg-gray-50 text-white dark:text-gray-800 text-sm md:text-base transition-colors duration-300"
          >
            <option value="featured">Featured</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="rating">Top Rated</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        {/* Organic Toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={organicOnly}
            onChange={(e) => onOrganicChange(e.target.checked)}
            className="w-4 h-4 md:w-5 md:h-5 rounded border-gray-300 dark:border-gray-400 text-accent dark:text-primary focus:ring-accent dark:focus:ring-primary transition-colors duration-300"
          />
          <span className="text-sm font-medium text-gray-300 dark:text-gray-600 transition-colors duration-300">Organic Only</span>
        </label>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            leftIcon={<X className="w-4 h-4" />}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductFilter;